import { Check } from "@/icons/Check.tsx";
import {
  accessoriesColors,
  getAccessoriesColor,
  prettifyString,
} from "@/lib/helpers.ts";
import {
  Card,
  Chip,
  Group,
  Image,
  Paper,
  Select,
  SelectProps,
} from "@mantine/core";
import { VariantProps } from "class-variance-authority";
import { useMemo } from "react";

type Props = {
  value: string | null | undefined;
  setValue: (value: string | null) => void;
  group: string;
  image: string;
  label: string;
};

export function AccessoriesCard({
  value,
  setValue,
  group,
  image,
  label,
}: Props) {
  const defaultValue = `${group}_random`;

  const renderSelectOption: SelectProps["renderOption"] = ({
    option,
    checked,
  }) => (
    <Group flex="1" gap="xs">
      <Paper
        h={20}
        w={20}
        radius="sm"
        style={{
          backgroundColor: getAccessoriesColor({
            color: option.value.substring(
              option.value.indexOf("_") + 1,
            ) as VariantProps<typeof getAccessoriesColor>["color"],
          }),
        }}
      />
      {option.label}
      {checked && <Check style={{ marginInlineStart: "auto" }} />}
    </Group>
  );

  const styles = useMemo(() => {
    return accessoriesColors.map((color) => ({
      value: `${group}_${color}`,
      label: prettifyString(color),
    }));
  }, [group]);

  return (
    <Card withBorder>
      <Card.Section withBorder>
        <div className="relative">
          <Image
            onClick={() => {
              if (!value) {
                setValue(defaultValue);
              } else {
                setValue(null);
              }
            }}
            maw={250}
            className="z-[2] cursor-pointer"
            src={image}
          />
          <Chip
            checked={value !== null}
            onChange={(v) => {
              if (v) {
                setValue(defaultValue);
              } else {
                setValue(null);
              }
            }}
            variant="filled"
            size="md"
            className="absolute w-full bottom-0 p-2 opacity-90"
            styles={{
              label: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            {label}
          </Chip>
        </div>
      </Card.Section>
      <Card.Section withBorder p="xs">
        <Select
          value={value}
          onChange={setValue}
          clearable
          placeholder="Select color"
          renderOption={renderSelectOption}
          data={styles}
        />
      </Card.Section>
    </Card>
  );
}
